import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
	FilterIcon,
	SearchIcon,
	ChevronDownIcon,
} from '@heroicons/react/outline';
import { useNotificationHistory } from '../../hooks/useEngageApi';

// Memoized status options to prevent recreation
const statusOptions = [
	{ value: 'all', label: 'All Status' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'scheduled', label: 'Scheduled' },
	{ value: 'paused', label: 'Paused' },
];

// Memoized table headers
const tableHeaders = [
	'Notification Title',
	'Type',
	'Trigger Date',
	'Delivered',
	'Opened',
	'Status',
];

// Optimized status badge component
const StatusBadge = React.memo(({ status }) => {
	const statusStyles = {
		Completed: 'text-blue-500 bg-blue-100',
		Scheduled: 'text-yellow-700 bg-yellow-100',
		Paused: 'text-gray-700 bg-gray-100',
	};

	return (
		<span
			className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[status] || 'text-gray-700 bg-gray-100'}`}
		>
			{status}
		</span>
	);
});

StatusBadge.displayName = 'StatusBadge';

// Optimized empty state component
const EmptyState = React.memo(() => (
	<div className="flex flex-col items-center justify-center py-12 h-96">
		<h3 className="text-lg font-medium text-gray-900 mb-1">No data found</h3>
		<p className="text-gray-500">
			As soon as there is an activity, the information will appear here.
		</p>
	</div>
));

EmptyState.displayName = 'EmptyState';

// Optimized search input component
const SearchInput = React.memo(({ searchQuery, onSearchChange, isSearching, onClear }) => (
	<div className="flex flex-row items-center border border-gray-300 px-2">
		{isSearching ? (
			<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1"></div>
		) : (
			<SearchIcon className="w-5 h-5 text-gray-500" />
		)}
		<input
			type="text"
			placeholder="Search for notifications"
			className="border-0 outline-none placeholder-gray-300 focus:ring-0"
			value={searchQuery}
			onChange={onSearchChange}
		/>
		{searchQuery && (
			<button
				onClick={onClear}
				className="ml-2 text-gray-400 hover:text-gray-600"
				title="Clear search"
			>
				×
			</button>
		)}
	</div>
));

SearchInput.displayName = 'SearchInput';

// Optimized filter dropdown component
const FilterDropdown = React.memo(({ statusFilter, isFilterOpen, onFilterToggle, onFilterSelect }) => (
	<div className="relative">
		<button
			className="flex flex-row items-center gap-2 border border-gray-300 px-2 py-1"
			onClick={onFilterToggle}
		>
			<FilterIcon className="w-4 h-4 text-gray-500" />
			<span className="text-gray-700">Filter</span>
			<ChevronDownIcon className="w-4 h-4 text-gray-500" />
		</button>
		{isFilterOpen && (
			<div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
				<div className="py-1">
					{statusOptions.map((option) => (
						<button
							key={option.value}
							className={`w-full text-left px-4 py-2 text-sm ${
								statusFilter === option.value
									? 'bg-blue-50 text-blue-700'
									: 'text-gray-700 hover:bg-gray-50'
							}`}
							onClick={() => onFilterSelect(option.value)}
						>
							{option.label}
						</button>
					))}
				</div>
			</div>
		)}
	</div>
));

FilterDropdown.displayName = 'FilterDropdown';

// Optimized pagination component
const Pagination = React.memo(({ 
	currentPage, 
	hasNextPage,
	filteredCount, 
	itemsPerPage,
	onPageChange 
}) => {
	const hasPreviousPage = currentPage > 1;

	const startIndex = (currentPage - 1) * itemsPerPage + 1;
	const endIndex = Math.min(currentPage * itemsPerPage, filteredCount);

	return (
		<div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
			<div className="flex-1 flex justify-between sm:hidden">
				<button
					onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
					disabled={!hasPreviousPage}
					className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
				>
					Previous
				</button>
				<button
					onClick={() => onPageChange(currentPage + 1)}
					disabled={!hasNextPage}
					className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
				>
					Next
				</button>
			</div>
			<div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
				<div>
					<p className="text-sm text-gray-700">
						Showing <span className="font-medium">{startIndex}</span> to{' '}
						<span className="font-medium">{endIndex}</span> on page {currentPage}
						{hasNextPage && <span> (more available)</span>}
					</p>
				</div>
				<div>
					<nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
						<button
							onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
							disabled={!hasPreviousPage}
							className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
						>
							Previous
						</button>
						<span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500">
							Page {currentPage}
						</span>
						<button
							onClick={() => onPageChange(currentPage + 1)}
							disabled={!hasNextPage}
							className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
						>
							Next
						</button>
					</nav>
				</div>
			</div>
		</div>
	);
});

Pagination.displayName = 'Pagination';

const NotificationHistory = ({ projectId }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState('all');
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const itemsPerPage = 10;

	// Debounce search query to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Reset page when search or filter changes
	useEffect(() => {
		setCurrentPage(1);
	}, [debouncedSearchQuery, statusFilter]);

	// API query parameters for server-side filtering and pagination
	const queryParams = useMemo(() => {
		const params = {
			page: currentPage.toString(),
		};
		
		// Add search parameter if provided
		if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
			params.search = debouncedSearchQuery.trim();
		}
		
		// Add filter_by parameter for status filtering (format: "field:value")
		if (statusFilter && statusFilter !== 'all') {
			params.filter_by = `status:${statusFilter}`;
		}
		
		return params;
	}, [currentPage, debouncedSearchQuery, statusFilter]);

	// Fetch notification history data
	const {
		data: historyData,
		isLoading,
		error,
	} = useNotificationHistory(projectId, queryParams);

	// Helper function to extract title and message from scheduled notifications
	// For scheduled notifications, data is stored in JSON format in the 'params' field
	const extractData = (params) => {
		if (!params) return { title: '-', message: '-' };
		try {
			const parsed = JSON.parse(params);
			return {
				title: parsed.post_title || parsed.title, // post_title is the primary field
				message: parsed.message
			};
		} catch {
			return { title: '-', message: '-' };
		}
	};

	// Helper function to format date
	const formatDate = (dateString) => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	// Helper function to format type
	const formatType = (type) => {
		if (!type) return 'Immediate';
		return type.charAt(0).toUpperCase() + type.slice(1);
	};

	// Helper function to format status
	const formatStatus = (userStatus, scheduledStatus, type) => {
		if (type === 'scheduled') {
			return scheduledStatus ? scheduledStatus.charAt(0).toUpperCase() + scheduledStatus.slice(1) : 'Scheduled';
		}
		return userStatus ? userStatus.charAt(0).toUpperCase() + userStatus.slice(1) : 'Completed';
	};

	// Helper function to format numbers
	const formatNumber = (num) => {
		if (num === null || num === undefined) return '-';
		if (num === 0) return '0';
		return num.toLocaleString();
	};

	// Transform API data to match expected format
	// Note: Filtering and search are now handled server-side via API parameters
	const notifications = useMemo(() => {
		// Handle different possible API response structures
		let dataArray = [];
		
		if (historyData?.data?.content && Array.isArray(historyData.data.content)) {
			// Structure: { data: { content: [...] } }
			dataArray = historyData.data.content;
		} else if (Array.isArray(historyData?.data)) {
			// Structure: { data: [...] }
			dataArray = historyData.data;
		} else if (Array.isArray(historyData)) {
			// Structure: [...] (direct array)
			dataArray = historyData;
		}
		
		if (dataArray.length === 0) {
			return [];
		}
		
		// Transform API response to match component expectations
		const transformedData = dataArray.map((item) => {
			return {
				id: item.id || item.notification_id || Math.random().toString(),
				title: item.title || item.post_title || item.notification_title || "-",
				message: item.message || item.notification_message || "-",
				type: formatType(item.notification_type?.toLowerCase() || item.type?.toLowerCase()),
				status: formatStatus(item.status, item.status, item.notification_type?.toLowerCase() || item.type?.toLowerCase()),
				date: formatDate(item.sort_timestamp || item.created_at || item.timestamp || item.date),
				delivered: item.delivered || item.delivered_count || "-",
				opened: item.opened || item.opened_count || "-",
			};
		});

		// Apply client-side filtering as fallback if server-side filtering doesn't work
		let filteredData = transformedData;
		
		// Apply search filter
		if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
			const searchTerm = debouncedSearchQuery.toLowerCase().trim();
			filteredData = filteredData.filter(item => 
				item.title.toLowerCase().includes(searchTerm) ||
				item.message.toLowerCase().includes(searchTerm) ||
				item.type.toLowerCase().includes(searchTerm) ||
				item.status.toLowerCase().includes(searchTerm)
			);
		}
		
		// Apply status filter
		if (statusFilter && statusFilter !== 'all') {
			const statusTerm = statusFilter.toLowerCase();
			filteredData = filteredData.filter(item => 
				item.status.toLowerCase() === statusTerm
			);
		}

		return filteredData;
	}, [historyData, debouncedSearchQuery, statusFilter]);

	// Server-side pagination data from API response
	const paginationData = useMemo(() => {
		// Handle different pagination structures
		let hasNextPage = false;
		let totalElements = 0;
		let currentPageCount = 0;
		
		if (historyData?.data) {
			// Try different pagination field names
			hasNextPage = !historyData.data.last && !historyData.data.is_last && !historyData.data.has_next_page;
			totalElements = historyData.data.total_elements || historyData.data.total || historyData.data.total_count || 0;
			currentPageCount = historyData.data.number_of_elements || historyData.data.current_count || historyData.data.count || 0;
		}
		
		// If we can't determine pagination from API, use the notifications length
		if (totalElements === 0) {
			totalElements = notifications.length;
		}
		if (currentPageCount === 0) {
			currentPageCount = notifications.length;
		}
		
		return { 
			hasNextPage,
			paginatedNotifications: notifications,
			totalElements,
			numberOfElements: currentPageCount
		};
	}, [historyData, notifications]);

	// Memoized event handlers
	const handleSearchChange = useCallback((e) => {
		setSearchQuery(e.target.value);
	}, []);

	const handleFilterToggle = useCallback(() => {
		setIsFilterOpen(prev => !prev);
	}, []);

	const handleFilterSelect = useCallback((value) => {
		setStatusFilter(value);
		setIsFilterOpen(false);
	}, []);

	const handlePageChange = useCallback((page) => {
		setCurrentPage(page);
	}, []);

	const handleClearSearch = useCallback(() => {
		setSearchQuery('');
	}, []);

	// Check if we're searching (debouncing)
	const isSearching = searchQuery !== debouncedSearchQuery;

	return (
		<div className="p-6">
			<div className="mb-6 space-y-4">
				<h2 className="text-2xl font-medium text-gray-900">
					Notification History
				</h2>
				<div className="flex flex-row items-center justify-between">
					<SearchInput 
						searchQuery={searchQuery} 
						onSearchChange={handleSearchChange}
						isSearching={isSearching}
						onClear={handleClearSearch}
					/>
					<FilterDropdown
						statusFilter={statusFilter}
						isFilterOpen={isFilterOpen}
						onFilterToggle={handleFilterToggle}
						onFilterSelect={handleFilterSelect}
					/>
				</div>
			</div>

			<div className="bg-white overflow-hidden border border-gray-200">
				{isLoading ? (
					<div className="flex items-center justify-center py-12 h-96">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
					</div>
				) : error ? (
					<div className="flex flex-col items-center justify-center py-12 h-96">
						<h3 className="text-lg font-medium text-gray-900 mb-1">Error loading data</h3>
						<p className="text-gray-500">Please try again later.</p>
					</div>
				) : notifications.length === 0 ? (
					<EmptyState />
				) : (
					<>
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-blue-600">
								<tr>
									{tableHeaders.map((header, index) => (
										<th
											key={index}
											className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider"
										>
											{header}
										</th>
									))}
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{paginationData.paginatedNotifications.map((notification) => (
									<tr key={notification.id}>
										<td className="px-6 py-4 whitespace-nowrap text-gray-900">
											<p className="text-sm font-medium">
												{notification.title}
											</p>
											<span className="text-xs text-gray-500">
												{notification.message}
											</span>
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{notification.type}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{notification.date}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{formatNumber(notification.delivered)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{formatNumber(notification.opened)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											<StatusBadge status={notification.status} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<Pagination
							currentPage={currentPage}
							hasNextPage={paginationData.hasNextPage}
							filteredCount={paginationData.totalElements || notifications.length}
							itemsPerPage={itemsPerPage}
							onPageChange={handlePageChange}
						/>
					</>
				)}
			</div>
		</div>
	);
};

export default React.memo(NotificationHistory);
